import { ComponentChildren, h, VNode } from "preact";
import { CSSProperties } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import { useTranslationContext } from "../context/translation.js";

export function CopyIcon(): VNode {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      style={{ width: 24, height: 24 }}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
      />
    </svg>
  );
}

export function CopiedIcon(): VNode {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      style={{ width: 24, height: 24 }}
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

export function CopyButton({
  class: clazz,
  style,
  children,
  getContent,
  onCopyComplete,
}: {
  children?: ComponentChildren;
  class: string;
  style?: CSSProperties;
  getContent: () => string;
  onCopyComplete?: () => void;
}): VNode {
  const [copied, setCopied] = useState(false);
  const { i18n } = useTranslationContext();
  async function copyText(): Promise<void> {
    const content = getContent() || "";
    try {
      if (!navigator.clipboard) throw Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(content);
      setCopied(true);
    } catch (error) {
      console.error("Could not write to the clipboard", error);
      prompt(i18n.str`Copy this text manually:`, content);
    } finally {
      onCopyComplete?.();
    }
  }
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!copied) {
    return (
      <button
        class={clazz}
        style={style}
        onClick={(e) => {
          e.preventDefault();
          void copyText();
        }}
      >
        <CopyIcon />
        {children}
      </button>
    );
  }
  return (
    <button class={clazz} style={style} disabled>
      <CopiedIcon />
      {children}
    </button>
  );
}
